import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '../store';
import { useLightMode } from '../utils';
import { AdminService } from '../api/AdminAPI';
import { StaffService } from '../api/StaffAPI';
import { NotifyService } from '../api/NotifyAPI';
import { Header } from '../components/Header/Header';
import { AddStaffForm } from '../components/AddStaffForm/AddStaffForm';
import { ResetPasswordForm } from '../components/ResetPasswordForm/ResetPasswordForm';
import { NotifySendModal } from '../components/NotifyModal/NotifySendModal';
import { NotifyTable } from '../components/NotifyTable/NotifyTable';
import { AdminTable } from '../components/AdminTable/AdminTable';
import s from '../styles/staff.module.css';

export function AdminTablePage() {
  const { user } = useAuth();
  const [lightMode, toggleLightMode] = useLightMode();
  const isAdmin = user?.role === 'admin';

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['staff-accounts'],
    queryFn:  () => AdminService.list(),
  });

  const staff = data?.staff || [];

  // The Square roster, so an admin can say which technician each login is.
  // Only an admin can act on it, so only an admin fetches it.
  const { data: rosterData } = useQuery({
    queryKey: ['staff-roster'],
    queryFn: () => StaffService.list('active'),
    enabled: isAdmin,
    staleTime: Infinity,
  });
  const roster = rosterData?.staffs || [];
  const [linkError, setLinkError] = useState('');

  const handleLinkStaff = useCallback(async (member, idStaff) => {
    setLinkError('');
    try {
      await AdminService.linkStaff(member.idUser, idStaff);
      await refetch();
    } catch (err) {
      setLinkError(err?.error || err?.message || 'Could not link that account');
    }
  }, [refetch]);

  const handleCreate = useCallback(async (form) => {
    try {
      await AdminService.create(form);
      await refetch();
      return { status: 'success' };
    } catch (err) {
      return { status: 'error', message: err?.message || 'Failed to add staff' };
    }
  }, [refetch]);


  const [notifyTarget, setNotifyTarget] = useState(null);
  const [resetTarget, setResetTarget]   = useState(null);
  const [showAddStaff, setShowAddStaff] = useState(false);

  const handleNotify = (staffMember) => () => setNotifyTarget(staffMember);
  const handleResetPassword = (staffMember) => setResetTarget(staffMember);

  const handleReset = useCallback(async (password, currentPassword) => {
    if (!resetTarget) return { status: 'error', message: 'No account selected' };
    try {
      await AdminService.resetPassword(resetTarget.idUser, password, currentPassword);
      return { status: 'success' };
    } catch (err) {
      return { status: 'error', message: err?.message || 'Failed to reset password' };
    }
  }, [resetTarget]);

  const handleSendNotify = useCallback(async ({ receiver, message, colors }) => {
    await NotifyService.send({ receiver, message, colors });
  }, []);

  return (
    <div className={lightMode ? `${s.page} ${s.lightPage}` : s.page}>
      <Header lightMode={lightMode} toggleLightMode={toggleLightMode} />

      <div className={s.body} style={{ gridTemplateColumns: '1fr' }}>
        <div>
          {isAdmin && (
            <div className={s.tableHeaderRow}>
              <div className={s.sectionLabel} style={{ marginBottom: 0 }}>Access Page</div>
              <button onClick={() => setShowAddStaff(true)} className={s.primaryBtn}>Add Admin</button>
            </div>
          )}
          {linkError && <div className={s.attnError}>{linkError}</div>}
          <AdminTable
            users={staff}
            isLoading={isLoading}
            isAdmin={isAdmin}
            currentUserId={user?.id}
            roster={roster}
            onNotify={handleNotify}
            onResetPassword={handleResetPassword}
            onLinkStaff={handleLinkStaff}
          />
          {isAdmin ? <NotifyTable /> : null}
        </div>
      </div>

      {notifyTarget ? (
        <NotifySendModal
          receiver={notifyTarget}
          onClose={() => setNotifyTarget(null)}
          onSend={handleSendNotify}
          light={lightMode}
        />
      ) : null}

      {resetTarget ? (
        <div className={s.modalOverlay} onClick={() => setResetTarget(null)}>
          <div className={s.modalBody} onClick={(e) => e.stopPropagation()}>
            <ResetPasswordForm
              target={resetTarget}
              isSelf={resetTarget.idUser === user?.id}
              onReset={handleReset}
              light={lightMode}
              onClose={() => setResetTarget(null)}
            />
          </div>
        </div>
      ) : null}

      {showAddStaff ? (
        <div className={s.modalOverlay} onClick={() => setShowAddStaff(false)}>
          <div className={s.modalBody} onClick={(e) => e.stopPropagation()}>
            <AddStaffForm
              onCreate={handleCreate}
              light={lightMode}
              onClose={() => setShowAddStaff(false)}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
